import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  X,
  Package,
  ChevronRight,
  ChevronLeft,
  Play,
  Pause,
  StopCircle,
  Download,
  CheckCircle,
  AlertCircle,
  Loader2,
  FileArchive,
  Settings2,
  Trash2,
  Save,
  RotateCcw,
  AlertTriangle,
  Monitor,
  Smartphone,
  Maximize,
} from 'lucide-react';
import { useBulkExportStore } from '../../../stores/bulkExportStore';
import { useStudioStore } from '../../../stores/studioStore';
import { BrandPicker } from '../brand/BrandPicker';
import {
  EXPORT_PRESETS,
  generatePacketFilename,
  getScaleSuffix,
  UNBRANDED_LEGACY_ID,
} from '../../../types/export';
import type { ExportPreset, JPEGQuality } from '../../../types/export';
import { ReadingOrderModal } from './ReadingOrderModal';
import { isReadableElement } from '../../../lib/readingOrder';

interface BulkExportDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

type WizardStep = 'select' | 'configure' | 'running' | 'results';

const QUALITY_OPTIONS: { value: JPEGQuality; label: string; desc: string }[] = [
  { value: 'low', label: 'Low', desc: '~60%' },
  { value: 'medium', label: 'Medium', desc: '~80%' },
  { value: 'high', label: 'High', desc: '~92%' },
  { value: 'maximum', label: 'Max', desc: '100%' },
];

const SCALE_OPTIONS: { value: number; label: string; desc: string; Icon: typeof Monitor }[] = [
  { value: 1, label: '1x', desc: 'Standard', Icon: Monitor },
  { value: 2, label: '2x', desc: 'Retina', Icon: Smartphone },
  { value: 3, label: '3x', desc: 'Ultra', Icon: Maximize },
];

export function BulkExportDialog({ isOpen, onClose }: BulkExportDialogProps) {
  const {
    job,
    selectedPreset,
    selectedTargets,
    storeId,
    qualityOverride,
    scaleOverride,
    customPresets,
    setSelectedPreset,
    setStoreId,
    setQualityOverride,
    setScaleOverride,
    resetOverrides,
    loadUserPrefs,
    loadCustomPresets,
    saveCustomPreset,
    deleteCustomPreset,
    startBulkExport,
    pauseExport,
    resumeExport,
    cancelExport,
    reset,
  } = useBulkExportStore();

  const { activeProject } = useStudioStore();
  const canvasState = useStudioStore((s) => s.canvasState);
  const [step, setStep] = useState<WizardStep>('select');
  const [showReadingOrder, setShowReadingOrder] = useState(false);

  const hasReadableElements = useMemo(
    () => (canvasState?.elements ?? []).some(isReadableElement),
    [canvasState?.elements]
  );

  const isRunning = job?.status === 'running' || job?.status === 'paused';

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    (async () => {
      await loadCustomPresets();
      if (cancelled) return;
      await loadUserPrefs();
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, loadCustomPresets, loadUserPrefs]);

  useEffect(() => {
    if (job?.status === 'complete' || job?.status === 'failed') {
      setStep('results');
    }
  }, [job?.status]);

  const handleClose = useCallback(() => {
    if (isRunning) return;
    reset();
    setStep('select');
    onClose();
  }, [isRunning, onClose, reset]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isRunning) handleClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, isRunning, handleClose]);

  const proceedWithStart = async () => {
    setStep('running');
    await startBulkExport();
  };

  const handleStart = () => {
    if (hasReadableElements) {
      setShowReadingOrder(true);
    } else {
      proceedWithStart();
    }
  };

  if (!isOpen) return null;

  return (
    <>
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={!isRunning ? handleClose : undefined}
      />

      <div className="relative z-10 max-w-2xl w-full mx-4 bg-[#1a1a2e] border border-white/[0.1] rounded-xl shadow-2xl overflow-hidden flex flex-col max-h-[85vh]">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/[0.06]">
          <div className="flex items-center gap-3">
            <Package size={18} className="text-cyan-400" />
            <h2 className="text-lg font-semibold text-white font-['BioRhyme']">Bulk Export</h2>
          </div>
          <div className="flex items-center gap-4">
            <StepIndicator step={step} />
            {!isRunning && (
              <button
                onClick={handleClose}
                className="p-1.5 rounded-lg text-white/40 hover:text-white hover:bg-white/[0.06] transition-colors"
              >
                <X size={18} />
              </button>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto sidebar-scroll">
          {step === 'select' && <SelectStep />}
          {step === 'configure' && (
            <ConfigureStep
              preset={selectedPreset}
              storeId={storeId}
              qualityOverride={qualityOverride}
              scaleOverride={scaleOverride}
              customPresets={customPresets}
              onPresetChange={setSelectedPreset}
              onStoreIdChange={setStoreId}
              onQualityChange={setQualityOverride}
              onScaleChange={setScaleOverride}
              onResetOverrides={resetOverrides}
              onSaveCustomPreset={saveCustomPreset}
              onDeleteCustomPreset={deleteCustomPreset}
            />
          )}
          {step === 'running' && <RunningStep />}
          {step === 'results' && <ResultsStep />}
        </div>

        <div className="flex items-center justify-between px-6 py-4 border-t border-white/[0.06]">
          {step === 'select' && (
            <>
              <button onClick={handleClose} className="px-4 py-2 rounded-lg text-white/60 text-sm hover:bg-white/[0.06] transition-colors">
                Cancel
              </button>
              <button
                onClick={() => setStep('configure')}
                disabled={selectedTargets.length === 0}
                className={`flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-medium transition-colors ${
                  selectedTargets.length > 0
                    ? 'bg-cyan-500 text-white hover:bg-cyan-600'
                    : 'bg-white/5 text-white/20 cursor-not-allowed'
                }`}
              >
                Configure
                <ChevronRight size={14} />
              </button>
            </>
          )}

          {step === 'configure' && (
            <>
              <button
                onClick={() => setStep('select')}
                className="flex items-center gap-1 px-4 py-2 rounded-lg text-white/60 text-sm hover:bg-white/[0.06] transition-colors"
              >
                <ChevronLeft size={14} />
                Back
              </button>
              <button
                onClick={handleStart}
                disabled={!activeProject}
                className="flex items-center gap-2 px-5 py-2 rounded-lg bg-cyan-500 text-white text-sm font-medium hover:bg-cyan-600 transition-colors"
              >
                <Play size={14} />
                Start Export ({selectedTargets.length} brands)
              </button>
            </>
          )}

          {step === 'running' && (
            <>
              <div className="flex items-center gap-2 text-white/40 text-sm">
                <Loader2 size={16} className="animate-spin" />
                {job?.status === 'paused' ? 'Paused' : 'Exporting...'}
              </div>
              <div className="flex gap-2">
                {job?.status === 'paused' ? (
                  <button
                    onClick={resumeExport}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-cyan-500/20 text-cyan-400 text-sm hover:bg-cyan-500/30 transition-colors"
                  >
                    <Play size={14} />
                    Resume
                  </button>
                ) : (
                  <button
                    onClick={pauseExport}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-amber-500/20 text-amber-400 text-sm hover:bg-amber-500/30 transition-colors"
                  >
                    <Pause size={14} />
                    Pause
                  </button>
                )}
                <button
                  onClick={cancelExport}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-red-500/20 text-red-400 text-sm hover:bg-red-500/30 transition-colors"
                >
                  <StopCircle size={14} />
                  Cancel
                </button>
              </div>
            </>
          )}

          {step === 'results' && (
            <>
              <div className="flex items-center gap-2 text-sm">
                {job?.status === 'complete' ? (
                  <>
                    <CheckCircle size={16} className="text-emerald-400" />
                    <span className="text-emerald-400">
                      {job.completedCount}/{job.totalCount} exported
                      {job.failedCount > 0 && ` (${job.failedCount} failed)`}
                    </span>
                  </>
                ) : (
                  <>
                    <AlertCircle size={16} className="text-red-400" />
                    <span className="text-red-400">Export failed</span>
                  </>
                )}
              </div>
              <div className="flex gap-2">
                {job?.zipBlobUrl && (
                  <a
                    href={job.zipBlobUrl}
                    download={`bulk-export-${job.id}.zip`}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-cyan-500 text-white text-sm font-medium hover:bg-cyan-600 transition-colors"
                  >
                    <Download size={14} />
                    Download ZIP
                  </a>
                )}
                <button
                  onClick={handleClose}
                  className="px-4 py-2 rounded-lg text-white/60 text-sm hover:bg-white/[0.06] transition-colors"
                >
                  Done
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>

    <ReadingOrderModal
      isOpen={showReadingOrder}
      onClose={() => setShowReadingOrder(false)}
      onConfirm={() => {
        setShowReadingOrder(false);
        proceedWithStart();
      }}
    />
    </>
  );
}

function StepIndicator({ step }: { step: WizardStep }) {
  const steps: { key: WizardStep; label: string }[] = [
    { key: 'select', label: 'Select' },
    { key: 'configure', label: 'Configure' },
    { key: 'running', label: 'Export' },
    { key: 'results', label: 'Results' },
  ];

  const currentIndex = steps.findIndex((s) => s.key === step);

  return (
    <div className="flex items-center gap-1">
      {steps.map((s, i) => (
        <div key={s.key} className="flex items-center gap-1">
          <div
            className={`w-1.5 h-1.5 rounded-full transition-colors ${
              i <= currentIndex ? 'bg-cyan-400' : 'bg-white/10'
            }`}
          />
          <span
            className={`text-[10px] ${
              i === currentIndex ? 'text-cyan-400' : i < currentIndex ? 'text-white/40' : 'text-white/15'
            }`}
          >
            {s.label}
          </span>
          {i < steps.length - 1 && <ChevronRight size={10} className="text-white/10 mx-0.5" />}
        </div>
      ))}
    </div>
  );
}

function SelectStep() {
  return (
    <div className="h-[400px]">
      <BrandPicker />
    </div>
  );
}

function ConfigureStep({
  preset,
  storeId,
  qualityOverride,
  scaleOverride,
  customPresets,
  onPresetChange,
  onStoreIdChange,
  onQualityChange,
  onScaleChange,
  onResetOverrides,
  onSaveCustomPreset,
  onDeleteCustomPreset,
}: {
  preset: ExportPreset;
  storeId: string;
  qualityOverride: JPEGQuality | null;
  scaleOverride: number | null;
  customPresets: ExportPreset[];
  onPresetChange: (p: ExportPreset) => void;
  onStoreIdChange: (storeId: string) => void;
  onQualityChange: (q: JPEGQuality | null) => void;
  onScaleChange: (s: number | null) => void;
  onResetOverrides: () => void;
  onSaveCustomPreset: (label: string) => Promise<ExportPreset | null>;
  onDeleteCustomPreset: (id: string) => Promise<void>;
}) {
  const { activeProject, schools, canvasState } = useStudioStore();
  const { selectedTargets } = useBulkExportStore();

  const [savingPreset, setSavingPreset] = useState(false);
  const [newPresetLabel, setNewPresetLabel] = useState('');
  const [saveError, setSaveError] = useState<string | null>(null);

  const effectiveQuality = qualityOverride ?? preset.config.quality;
  const effectiveScale = scaleOverride ?? preset.config.scale;
  const isModified =
    (qualityOverride !== null && qualityOverride !== preset.config.quality) ||
    (scaleOverride !== null && scaleOverride !== preset.config.scale);
  const qualityVisible = preset.config.format === 'jpeg' || preset.config.format === 'webp';

  const sampleTarget = selectedTargets[0];
  const sampleSchool = sampleTarget ? schools.find((s) => s.id === sampleTarget.schoolId) : undefined;
  const sampleSchoolId = sampleSchool?.legacy_id || sampleTarget?.subdomain || UNBRANDED_LEGACY_ID;
  const projectName = activeProject?.name || 'project-name';
  const imageExt = preset.config.format === 'jpeg' ? 'jpg' : preset.config.format;
  const scaleSuffix = getScaleSuffix(effectiveScale);
  const stem = generatePacketFilename(sampleSchoolId, projectName, storeId || '0');
  const bgFilename = `${stem}${scaleSuffix}.${imageExt}`;
  const overlayFilename = `${stem}.txt`;

  const dims = canvasState?.dimensions;
  const scaledW = Math.round((dims?.width || 1920) * effectiveScale);
  const scaledH = Math.round((dims?.height || 1080) * effectiveScale);

  const allPresets = useMemo<ExportPreset[]>(
    () => [...EXPORT_PRESETS, ...customPresets],
    [customPresets]
  );

  const handleSave = async () => {
    const label = newPresetLabel.trim();
    if (!label) {
      setSaveError('Label is required');
      return;
    }
    setSaveError(null);
    const created = await onSaveCustomPreset(label);
    if (created) {
      setSavingPreset(false);
      setNewPresetLabel('');
    } else {
      setSaveError('Could not save (label may already exist)');
    }
  };

  return (
    <div className="px-6 py-5 space-y-6">
      <div>
        <div className="flex items-center justify-between mb-3">
          <label className="block text-sm font-medium text-white/60">Export Preset</label>
          {customPresets.length > 0 && (
            <span className="text-[10px] text-white/25 uppercase tracking-wider">
              {customPresets.length} custom
            </span>
          )}
        </div>
        <div className="space-y-2">
          {allPresets.map((p) => {
            const isActive = preset.id === p.id;
            const isCustom = p.id.startsWith('custom:');
            return (
              <div
                key={p.id}
                className={`relative flex items-start gap-3 p-3 rounded-lg border transition-all ${
                  isActive
                    ? 'border-cyan-500 bg-cyan-500/[0.06]'
                    : 'border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.04]'
                }`}
              >
                <button
                  type="button"
                  onClick={() => onPresetChange(p)}
                  className="absolute inset-0 w-full h-full rounded-lg"
                  aria-label={`Select ${p.label}`}
                />
                <Settings2
                  size={16}
                  className={`mt-0.5 flex-shrink-0 relative pointer-events-none ${isActive ? 'text-cyan-400' : 'text-white/20'}`}
                />
                <div className="flex-1 min-w-0 relative pointer-events-none">
                  <div className="flex items-center gap-2">
                    <div className={`text-sm font-medium truncate ${isActive ? 'text-white' : 'text-white/60'}`}>
                      {p.label}
                    </div>
                    {isCustom && (
                      <span className="px-1.5 py-0.5 text-[9px] uppercase font-medium rounded bg-cyan-500/10 text-cyan-400">
                        Custom
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-white/30 mt-0.5 truncate">{p.description}</div>
                  <div className="flex flex-wrap gap-2 mt-1.5">
                    <span className="px-1.5 py-0.5 text-[9px] uppercase font-medium rounded bg-white/[0.04] text-white/30">
                      {p.config.format}
                    </span>
                    <span className="px-1.5 py-0.5 text-[9px] uppercase font-medium rounded bg-white/[0.04] text-white/30">
                      {p.config.quality}
                    </span>
                    <span className="px-1.5 py-0.5 text-[9px] uppercase font-medium rounded bg-white/[0.04] text-white/30">
                      {p.config.scale}x
                    </span>
                    {p.config.includeOverlay && (
                      <span className="px-1.5 py-0.5 text-[9px] uppercase font-medium rounded bg-emerald-500/10 text-emerald-400">
                        + overlay
                      </span>
                    )}
                  </div>
                </div>
                {isCustom && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteCustomPreset(p.id);
                    }}
                    className="relative flex-shrink-0 p-1 rounded text-white/20 hover:text-red-400 hover:bg-red-500/10 transition-colors z-10"
                    title="Delete custom preset"
                    aria-label={`Delete ${p.label}`}
                  >
                    <Trash2 size={12} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <label className="block text-sm font-medium text-white/60">Quality Controls</label>
          {isModified && (
            <div className="flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-wider text-amber-400 font-medium">
                Modified
              </span>
              <button
                onClick={onResetOverrides}
                className="flex items-center gap-1 px-2 py-1 rounded text-[10px] text-white/50 hover:text-white hover:bg-white/[0.06] transition-colors"
              >
                <RotateCcw size={10} />
                Reset
              </button>
            </div>
          )}
        </div>

        {qualityVisible ? (
          <div className="mb-3">
            <div className="text-[11px] font-medium text-white/40 mb-1.5">Quality</div>
            <div className="flex gap-1 p-1 rounded-lg bg-[#12121a]">
              {QUALITY_OPTIONS.map((opt) => {
                const isActive = effectiveQuality === opt.value;
                const isDefault = opt.value === preset.config.quality;
                return (
                  <button
                    key={opt.value}
                    onClick={() =>
                      onQualityChange(opt.value === preset.config.quality ? null : opt.value)
                    }
                    className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-all ${
                      isActive
                        ? 'bg-cyan-500/20 text-cyan-400 shadow-sm'
                        : 'text-white/40 hover:text-white/60'
                    }`}
                  >
                    <div className="flex items-center justify-center gap-1">
                      <span>{opt.label}</span>
                      {isDefault && (
                        <span className="text-[8px] text-white/20 uppercase">def</span>
                      )}
                    </div>
                    <div className="text-[10px] mt-0.5 opacity-60">{opt.desc}</div>
                  </button>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="mb-3 px-3 py-2 rounded-lg bg-[#0a0a0f] border border-white/[0.04] text-[11px] text-white/40">
            PNG is lossless — quality is fixed at 100%.
          </div>
        )}

        <div>
          <div className="text-[11px] font-medium text-white/40 mb-1.5">Scale</div>
          <div className="flex gap-2">
            {SCALE_OPTIONS.map((opt) => {
              const isActive = effectiveScale === opt.value;
              const isDefault = opt.value === preset.config.scale;
              return (
                <button
                  key={opt.value}
                  onClick={() =>
                    onScaleChange(opt.value === preset.config.scale ? null : opt.value)
                  }
                  className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-3 rounded-lg border transition-all ${
                    isActive
                      ? 'border-cyan-500 bg-cyan-500/10 text-white'
                      : 'border-white/[0.06] bg-white/[0.02] text-white/60 hover:bg-white/[0.06]'
                  }`}
                >
                  <opt.Icon size={16} className={isActive ? 'text-cyan-400' : ''} />
                  <div className="text-left">
                    <div className="text-sm font-medium flex items-center gap-1">
                      <span>{opt.label}</span>
                      {isDefault && <span className="text-[8px] text-white/20 uppercase">def</span>}
                    </div>
                    <div className="text-[10px] opacity-60">{opt.desc}</div>
                  </div>
                </button>
              );
            })}
          </div>
          <div className="mt-2 text-[10px] text-white/30">
            Output: <span className="text-white/60 font-mono">{scaledW}x{scaledH}px</span>
            {effectiveScale === 3 && (
              <span className="ml-2 inline-flex items-center gap-1 text-amber-400/70">
                <AlertTriangle size={10} />
                ~9x file size — expect longer export time
              </span>
            )}
          </div>
        </div>

        <div className="mt-4 pt-4 border-t border-white/[0.04]">
          {savingPreset ? (
            <div className="space-y-2">
              <div className="text-[11px] font-medium text-white/50">
                Save current settings as a reusable preset
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  autoFocus
                  value={newPresetLabel}
                  onChange={(e) => {
                    setNewPresetLabel(e.target.value);
                    setSaveError(null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSave();
                    if (e.key === 'Escape') {
                      setSavingPreset(false);
                      setNewPresetLabel('');
                      setSaveError(null);
                    }
                  }}
                  placeholder="e.g. Print Max Retina"
                  className="flex-1 px-3 py-1.5 rounded-lg bg-[#12121a] border border-white/[0.1] text-sm text-white placeholder:text-white/20 outline-none focus:border-cyan-500/50 transition-colors"
                />
                <button
                  onClick={handleSave}
                  className="px-3 py-1.5 rounded-lg bg-cyan-500 text-white text-xs font-medium hover:bg-cyan-600 transition-colors"
                >
                  Save
                </button>
                <button
                  onClick={() => {
                    setSavingPreset(false);
                    setNewPresetLabel('');
                    setSaveError(null);
                  }}
                  className="px-3 py-1.5 rounded-lg text-white/50 text-xs hover:bg-white/[0.06] transition-colors"
                >
                  Cancel
                </button>
              </div>
              {saveError && <div className="text-[10px] text-red-400">{saveError}</div>}
            </div>
          ) : (
            <button
              onClick={() => setSavingPreset(true)}
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-white/[0.08] text-xs text-white/60 hover:text-white hover:bg-white/[0.04] hover:border-cyan-500/30 transition-colors"
            >
              <Save size={12} />
              Save current as custom preset
            </button>
          )}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-white/60 mb-2">Filename Convention</label>
        <div className="px-3 py-2 rounded-lg bg-[#12121a] border border-white/[0.06] text-xs text-white/70 font-mono">
          schoolId_image-title_storeId
        </div>
        <div className="mt-1.5 text-[10px] text-white/30 leading-relaxed">
          <span className="text-white/50">schoolId</span> and{' '}
          <span className="text-white/50">legacy_id</span> are interchangeable (internally referred to
          as <span className="text-white/50">schoolId</span>). Image title is derived from the project
          name.
        </div>

        <div className="mt-3">
          <label className="block text-[11px] font-medium text-white/50 mb-1.5">Store ID</label>
          <input
            type="text"
            value={storeId}
            onChange={(e) => onStoreIdChange(e.target.value)}
            placeholder="0"
            className="w-32 px-3 py-1.5 rounded-lg bg-[#12121a] border border-white/[0.06] text-sm text-white font-mono placeholder:text-white/20 outline-none focus:border-cyan-500/30 transition-colors"
          />
          <div className="mt-1 text-[10px] text-white/25">Defaults to 0 when blank.</div>
        </div>

        <div className="mt-3 p-3 rounded-lg bg-[#0a0a0f] border border-white/[0.04] space-y-1.5">
          <div className="text-[10px] text-white/30 uppercase tracking-wider mb-1">
            Preview {sampleTarget ? `(${sampleTarget.schoolName})` : '(sample)'}
          </div>
          <div className="flex items-center gap-2">
            <span className="text-[9px] px-1.5 py-0.5 rounded bg-cyan-500/10 text-cyan-400 font-medium uppercase tracking-wider">
              Image
            </span>
            <div className="text-xs text-white/60 font-mono truncate">{bgFilename}</div>
          </div>
          {preset.config.includeOverlay && (
            <div className="flex items-center gap-2">
              <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400 font-medium uppercase tracking-wider">
                Overlay
              </span>
              <div className="text-xs text-white/60 font-mono truncate">{overlayFilename}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function RunningStep() {
  const { job } = useBulkExportStore();
  if (!job) return null;

  const overallPercent = job.totalCount > 0
    ? Math.round((job.items.reduce((sum, item) => sum + item.percent, 0) / (job.totalCount * 100)) * 100)
    : 0;

  return (
    <div className="px-6 py-5">
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-white/60">Overall Progress</span>
          <span className="text-sm font-mono text-white/40">
            {job.completedCount + job.failedCount}/{job.totalCount}
          </span>
        </div>
        <div className="h-2 bg-[#12121a] rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-cyan-500 to-cyan-400 rounded-full transition-all duration-300"
            style={{ width: `${overallPercent}%` }}
          />
        </div>
        <div className="text-right mt-1 text-[10px] text-white/25">{overallPercent}%</div>
      </div>

      <div className="space-y-1 max-h-[300px] overflow-y-auto sidebar-scroll">
        {job.items.map((item, idx) => {
          const target = job.targets[idx];
          return (
            <div
              key={item.brandId}
              className="flex items-center gap-3 px-3 py-2 rounded-lg bg-white/[0.01]"
            >
              <div className="flex-shrink-0 w-5 h-5 flex items-center justify-center">
                {item.status === 'pending' && <div className="w-2 h-2 rounded-full bg-white/10" />}
                {item.status === 'rendering' && <Loader2 size={14} className="animate-spin text-cyan-400" />}
                {item.status === 'complete' && <CheckCircle size={14} className="text-emerald-400" />}
                {item.status === 'failed' && <AlertCircle size={14} className="text-red-400" />}
                {item.status === 'skipped' && <div className="w-2 h-2 rounded-full bg-white/5" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs text-white/60 truncate">{target?.schoolName ?? item.brandId}</div>
                <div className="text-[10px] text-white/25 truncate">{item.message}</div>
              </div>
              {item.status === 'rendering' && (
                <div className="w-16">
                  <div className="h-1 bg-[#12121a] rounded-full overflow-hidden">
                    <div
                      className="h-full bg-cyan-500/60 rounded-full transition-all"
                      style={{ width: `${item.percent}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ResultsStep() {
  const { job } = useBulkExportStore();
  if (!job) return null;

  const succeeded = job.items.filter((i) => i.status === 'complete');
  const failed = job.items.filter((i) => i.status === 'failed');

  return (
    <div className="px-6 py-5 space-y-4">
      <div className="grid grid-cols-3 gap-3">
        <div className="p-3 rounded-lg bg-[#12121a] border border-white/[0.06] text-center">
          <div className="text-lg font-semibold text-white">{job.totalCount}</div>
          <div className="text-[10px] text-white/30 uppercase tracking-wider">Total</div>
        </div>
        <div className="p-3 rounded-lg bg-emerald-500/[0.06] border border-emerald-500/10 text-center">
          <div className="text-lg font-semibold text-emerald-400">{succeeded.length}</div>
          <div className="text-[10px] text-emerald-400/50 uppercase tracking-wider">Success</div>
        </div>
        <div className="p-3 rounded-lg bg-red-500/[0.06] border border-red-500/10 text-center">
          <div className="text-lg font-semibold text-red-400">{failed.length}</div>
          <div className="text-[10px] text-red-400/50 uppercase tracking-wider">Failed</div>
        </div>
      </div>

      {job.zipBlobUrl && (
        <div className="flex items-center gap-3 p-3 rounded-lg bg-cyan-500/[0.04] border border-cyan-500/10">
          <FileArchive size={20} className="text-cyan-400 flex-shrink-0" />
          <div className="flex-1">
            <div className="text-sm text-white">ZIP package ready</div>
            <div className="text-[10px] text-white/30">Contains all exported files</div>
          </div>
          <a
            href={job.zipBlobUrl}
            download={`bulk-export-${job.id}.zip`}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-cyan-500 text-white text-xs font-medium hover:bg-cyan-600 transition-colors"
          >
            <Download size={12} />
            Download
          </a>
        </div>
      )}

      {failed.length > 0 && (
        <div>
          <div className="text-xs font-medium text-red-400/60 mb-2">Failed Items</div>
          <div className="space-y-1 max-h-[200px] overflow-y-auto sidebar-scroll">
            {failed.map((item) => {
              const target = job.targets.find((t) => t.brandId === item.brandId);
              return (
                <div
                  key={item.brandId}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-500/[0.03] border border-red-500/[0.06]"
                >
                  <AlertCircle size={12} className="text-red-400 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs text-white/50 truncate">
                      {target?.schoolName ?? item.brandId}
                    </div>
                    <div className="text-[10px] text-red-400/50 truncate">{item.error}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
